import React, { useEffect, useState } from 'react';
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaBriefcase,
  FaRegCopy,
  FaUserEdit,
  FaCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './EmployeeCardModal.module.css';

const EMPTY_VALUES = ['nan', 'nat', 'n/a', 'null', 'undefined'];

const display = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const str = String(value).trim();
  if (!str || EMPTY_VALUES.includes(str.toLowerCase())) return '—';
  return str;
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = toDate(value);
  return d
    ? d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : display(value);
};

const ageYears = (value) => {
  const d = toDate(value);
  if (!d) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  return years > 0 && years < 130 ? years : null;
};

const yesNo = (value) =>
  value === 'Yes' ? 'Evet' : value === 'No' ? 'Hayır' : display(value);

const initials = (emp) =>
  `${(emp.eName || '').charAt(0)}${(emp.eSurname || '').charAt(0)}`.toUpperCase() || '?';

const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} kopyalandı`);
  } catch {
    toast.error('Kopyalanamadı');
  }
};

// Tek bilgi satırı; copyable verildiğinde hover'da kopyalama düğmesi gösterir
const InfoItem = ({ label, value, hint, copyable, href, wide }) => {
  const isEmpty = !value || value === '—';
  return (
    <div className={`${styles.infoItem} ${wide ? styles.infoItemWide : ''}`}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={`${styles.infoValue} ${isEmpty ? styles.infoValueEmpty : ''}`}>
        {href && !isEmpty ? (
          <a href={href} className={styles.infoLink}>{value}</a>
        ) : (
          value
        )}
        {hint && <span className={styles.infoHint}>{hint}</span>}
        {copyable && !isEmpty && (
          <button
            type="button"
            className={styles.copyButton}
            onClick={() => copyToClipboard(value, label)}
            title={`${label} kopyala`}
            aria-label={`${label} kopyala`}
          >
            <FaRegCopy />
          </button>
        )}
      </span>
    </div>
  );
};

const EmployeeCardModal = ({ employee, onClose, onEdit }) => {
  const { isDarkMode } = useTheme();
  // Profil sayfasından yüklenen fotoğraf Employees dokümanında profileImageUrl
  // alanında tutulur; yüklenemezse baş harflere geri dönülür
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!employee) return null;

  const isActive = Number(employee.Active) === 1;
  const age = ageYears(employee.birthday);
  const photoUrl = !photoFailed && employee.profileImageUrl ? employee.profileImageUrl : null;

  const heroChips = [
    employee.departman,
    employee.workType,
    employee.authorization
  ].filter((v) => display(v) !== '—');

  return (
    <div
      className={`${styles.overlay} ${isDarkMode ? styles.darkMode : ''}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Çalışan Kartı">
        {/* Hero */}
        <div className={styles.hero}>
          <button className={styles.closeButton} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
          <div className={styles.heroContent}>
            <span className={styles.avatar}>
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={`${employee.eName || ''} ${employee.eSurname || ''}`.trim() || 'Çalışan'}
                  className={styles.avatarImg}
                  onError={() => setPhotoFailed(true)}
                />
              ) : (
                initials(employee)
              )}
            </span>
            <div className={styles.heroInfo}>
              <h2 className={styles.heroName}>
                {display(`${employee.eName || ''} ${employee.eSurname || ''}`.trim())}
              </h2>
              <p className={styles.heroRole}>
                {display(employee.role) !== '—'
                  ? employee.role
                  : display(employee.profession)}
              </p>
              <div className={styles.heroChips}>
                <span className={`${styles.statusChip} ${isActive ? styles.statusChipActive : styles.statusChipInactive}`}>
                  <FaCircle /> {isActive ? 'Aktif' : 'İnaktif'}
                </span>
                {heroChips.map((chip) => (
                  <span key={chip} className={styles.heroChip}>{chip}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.body}>
          {/* Kişisel Bilgiler */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FaUser className={styles.sectionIcon} />
              Kişisel Bilgiler
            </h3>
            <div className={styles.infoGrid}>
              <InfoItem label="Sicil Numarası" value={display(employee.ID)} />
              <InfoItem label="TC Kimlik No" value={display(employee.tcNumber)} copyable />
              <InfoItem
                label="Doğum Tarihi"
                value={formatDate(employee.birthday)}
                hint={age ? `${age} yaşında` : null}
              />
              <InfoItem label="Uyruk" value={display(employee.nationality)} />
              <InfoItem label="Eğitim Bilgisi" value={display(employee.educationInfo)} />
              <InfoItem label="Dil Bilgisi" value={display(employee.languageInfo)} />
              <InfoItem label="Engellilik Durumu" value={yesNo(employee.isHandicappedPerson)} />
              <InfoItem label="Emekli Mi?" value={yesNo(employee.isRetired)} />
              <InfoItem label="Stajyer Mi?" value={yesNo(employee.isIntern)} />
            </div>
          </section>

          {/* İletişim */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FaEnvelope className={styles.sectionIcon} />
              İletişim
            </h3>
            <div className={`${styles.infoGrid} ${styles.infoGridContact}`}>
              <InfoItem
                label="E-posta"
                value={display(employee.eMail)}
                href={display(employee.eMail) !== '—' ? `mailto:${employee.eMail}` : null}
                copyable
              />
              <InfoItem
                label="Telefon"
                value={display(employee.phone)}
                href={display(employee.phone) !== '—' ? `tel:${employee.phone}` : null}
                copyable
              />
              <InfoItem
                label="Acil Durum Telefonu"
                value={display(employee.emergencyPhone)}
                href={
                  display(employee.emergencyPhone) !== '—'
                    ? `tel:${employee.emergencyPhone}`
                    : null
                }
              />
              <InfoItem label="Adres" value={display(employee.address)} wide />
            </div>
          </section>

          {/* İş Bilgileri */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FaBriefcase className={styles.sectionIcon} />
              İş Bilgileri
            </h3>
            <div className={styles.infoGrid}>
              <InfoItem label="Departman" value={display(employee.departman)} />
              <InfoItem label="Meslek" value={display(employee.profession)} />
              <InfoItem label="Çalışan Rolü" value={display(employee.role)} />
              <InfoItem label="Çalışma Şekli" value={display(employee.workType)} />
              <InfoItem label="İşe Başlama Tarihi" value={formatDate(employee.startDate)} />
              <InfoItem label="Yıllık İzin" value={display(employee.annualPermit)} />
              <InfoItem label="Yetkilendirme" value={display(employee.authorization)} />
            </div>
          </section>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            Kapat
          </button>
          {onEdit && (
            <button type="button" className={styles.primaryButton} onClick={onEdit}>
              <FaUserEdit />
              Düzenle
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeCardModal;
